"use client";

import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ResendChannel, type ResendOptions } from "lib/api-types/schemas";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { ApiError } from "@/lib/api-client/authenticatedClient";
import {
  getApiCcofPracticeIdPatientsPatientIdNotificationsResend,
  postApiCcofPracticeIdPatientsPatientIdNotificationsResend,
} from "@/lib/api-client/endpoints/patient-notification-resend/patient-notification-resend";
import { useBooleanFeatureFlag } from "@/lib/feature-flags/FeatureFlags";
import { FEATURE_FLAG_KEYS } from "@/lib/feature-flags/flags";
import { SIGNALR_EVENTS } from "@/lib/signalr/events";
import { useSignalRConnectionState } from "@/lib/signalr/SignalRContext";
import type { PatientArChangedEvent } from "@/lib/signalr/types";
import { useSignalREvent } from "@/lib/signalr/useSignalREvent";
import { statementHistoryQueryKey } from "@/features/patient-ar/queries/useStatementHistory";

export const HISTORY_REFRESH_DELAYS_MS = [1000, 3000, 6000, 10000, 20000];

export type ResendFailureReason =
  | "too_soon"
  | "recipient_changed"
  | "statement_changed"
  | "failed";

export type ResendOutcome =
  | { status: "sent" }
  | { status: "failed"; reason: ResendFailureReason; address: string | null };

const FAILURE_REASON_BY_CODE: Record<string, ResendFailureReason> = {
  resend_too_soon: "too_soon",
  recipient_changed: "recipient_changed",
  statement_changed: "statement_changed",
};

function getFailureReason(error: unknown): ResendFailureReason {
  if (!(error instanceof ApiError)) return "failed";
  const code = (error.body as { code?: string } | undefined)?.code;
  return (code && FAILURE_REASON_BY_CODE[code]) || "failed";
}

export const resendOptionsQueryKey = (practiceId: string, patientId: string) =>
  ["patient-notification-resend", practiceId, patientId] as const;

function getFailureAddress(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as { details?: { address?: string } } | undefined;
  return body?.details?.address ?? null;
}

export function useResendNotification(patientId: string | null) {
  const { tenantId } = useTenantRouteContext();
  const queryClient = useQueryClient();
  const isEnabled = useBooleanFeatureFlag(
    FEATURE_FLAG_KEYS.patientNotificationResend,
  );
  const enabled = isEnabled && patientId !== null;

  const { data: options } = useQuery<ResendOptions>({
    queryKey: resendOptionsQueryKey(tenantId, patientId ?? ""),
    queryFn: () =>
      getApiCcofPracticeIdPatientsPatientIdNotificationsResend(
        tenantId,
        patientId!,
      ).then((res) => res.data),
    enabled,
    staleTime: 0,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (
      body: Parameters<
        typeof postApiCcofPracticeIdPatientsPatientIdNotificationsResend
      >[2],
    ) =>
      postApiCcofPracticeIdPatientsPatientIdNotificationsResend(
        tenantId,
        patientId!,
        body,
      ),
  });

  const invalidateOptions = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: resendOptionsQueryKey(tenantId, patientId ?? ""),
      }),
    [queryClient, tenantId, patientId],
  );

  const historyTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearHistoryTimers = useCallback(() => {
    historyTimers.current.forEach(clearTimeout);
    historyTimers.current = [];
  }, []);

  useEffect(
    () => clearHistoryTimers,
    [clearHistoryTimers, tenantId, patientId],
  );

  const refreshHistoryAfterSend = useCallback(() => {
    const queryKey = statementHistoryQueryKey(tenantId, patientId ?? "");

    clearHistoryTimers();
    historyTimers.current = HISTORY_REFRESH_DELAYS_MS.map((delay) =>
      setTimeout(
        () =>
          void queryClient.invalidateQueries(
            { queryKey },
            { cancelRefetch: false },
          ),
        delay,
      ),
    );
  }, [queryClient, tenantId, patientId, clearHistoryTimers]);

  useSignalREvent<PatientArChangedEvent>(
    SIGNALR_EVENTS.PATIENT_AR_CHANGED,
    (event) => {
      if (event.practiceId !== tenantId || event.patientId !== patientId) {
        return;
      }
      void invalidateOptions();
    },
  );

  const isConnected = useSignalRConnectionState();
  const wasConnected = useRef(isConnected);

  useEffect(() => {
    const justConnected = isConnected && !wasConnected.current;
    wasConnected.current = isConnected;
    if (justConnected && enabled) void invalidateOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  const resend = useCallback(
    async (
      channel: ResendChannel,
      confirmed: ResendOptions,
    ): Promise<ResendOutcome> => {
      const address =
        channel === ResendChannel.Email
          ? confirmed.email?.address
          : confirmed.sms?.address;
      if (!enabled || !confirmed.statementId || !confirmed.notificationType) {
        return { status: "failed", reason: "failed", address: address ?? null };
      }

      try {
        await mutateAsync({
          statementId: confirmed.statementId,
          notificationType: confirmed.notificationType,
          channel,
          address,
        });
        refreshHistoryAfterSend();
        return { status: "sent" };
      } catch (error) {
        return {
          status: "failed",
          reason: getFailureReason(error),
          address: getFailureAddress(error) ?? address ?? null,
        };
      } finally {
        void invalidateOptions();
      }
    },
    [enabled, mutateAsync, invalidateOptions, refreshHistoryAfterSend],
  );

  return { isEnabled, options, isSending: isPending, resend };
}
